/**
 * 教室主界面：把白板、视频、成员、聊天组装成课堂布局。
 */

import { useEffect, useState } from 'react'

import { Chat } from '../components/Chat'
import { ClassTimer, NetIndicator } from '../components/HeadStatus'
import { DocImport } from '../components/DocImport'
import { FileTransfer } from '../components/FileTransfer'
import { MemberList } from '../components/MemberList'
import { WaitingPanel } from '../components/WaitingPanel'
import { CaptionLayer } from '../components/Captions'
import { GroupBanner } from '../components/Groups'
import { AttendCard, AttendProgress, AttendResult } from '../components/Attend'
import { BottomSheet } from '../components/BottomSheet'
import { PollCard } from '../components/Poll'
import { PollResult } from '../components/PollDialogs'
import { PrivateChat } from '../components/PrivateChat'
import { ReactionLayer } from '../components/Reactions'
import { ControlRequestPrompt } from '../components/RemoteControl'
import { Toolbar, WhiteboardTools } from '../components/Toolbar'
import { AudioSink, LocalPreview, VideoStage } from '../components/VideoStage'
import { Whiteboard } from '../components/Whiteboard'
import { RoomFlag } from '../lib/protocol'
import { useChatUnread } from '../lib/useChatUnread'
import { isMobileLandscape, useMobileLayout, useMobileLandscape } from '../lib/mobile'
import { useImmersive } from '../lib/useImmersive'
import { useCallRecovery, useNetToasts } from '../lib/useMobilePolish'
import { useRoomStore } from '../stores/roomStore'
import type { LoginResult } from './Login'
import { IcMegaphone } from '../components/Icons'

interface Props {
  session: LoginResult
  /** 在准备页选好的初始状态：进来就按这个开麦克风与摄像头。 */
  startWith?: { micOn: boolean; camOn: boolean }
  onExit: () => void
  /** 换房间（分组讨论进小组 / 回主房间）。房间状态在服务端，必须真的换过去 */
  onSwitchRoom?: (roomId: number) => void
}

type Tab = 'board' | 'video'

export function Classroom({ session, startWith, onExit, onSwitchRoom }: Props) {
  const client = useRoomStore((s) => s.client)
  const conn = useRoomStore((s) => s.conn)
  const room = useRoomStore((s) => s.room)
  const mobile = useMobileLayout()
  // 横屏手机进沉浸模式：三秒没操作，顶栏底栏自动收起，点内容区再唤出。
  const landscapeNow = useMobileLandscape()
  const landscape = mobile && landscapeNow
  const chromeHidden = useImmersive(landscape)
  // 手机专属：网络变化要说话、来电之后要自己缓过来
  useNetToasts(mobile)
  useCallRecovery(mobile)
  // 按房间记在 sessionStorage：刷新不重弹，换教室重弹，关掉标签页就忘。
  const noticeKey = `whatylive.notice.seen.${room?.id ?? 0}`
  const [noticeDismissed, setNoticeDismissed] = useState(() => {
    try { return sessionStorage.getItem(noticeKey) === '1' } catch { return false }
  })
  const dismissNotice = () => {
    setNoticeDismissed(true)
    try { sessionStorage.setItem(noticeKey, '1') } catch { /* 隐私模式写不进去，本次会话不再弹就行 */ }
  }
  const toast = useRoomStore((s) => s.toast)
  const recording = useRoomStore((s) => s.recording)
  const sharerId = useRoomStore((s) => s.sharerId)
  const setToast = useRoomStore((s) => s.setToast)
  const leave = useRoomStore((s) => s.leave)
  const leftReason = useRoomStore((s) => s.leftReason)

  const [tab, setTab] = useState<Tab>('board')
  const [localStream, setLocalStream] = useState<MediaStream | null>(null)
  const media = useRoomStore((s) => s.media)
  // 切虚拟背景时轨会被换掉，预览要跟着换。
  useEffect(() => {
    media?.setLocalStreamHandler((s) => setLocalStream(s))
    return () => media?.setLocalStreamHandler(null)
  }, [media])
  const [importing, setImporting] = useState(false)
  const [sideTab, setSideTab] = useState<'chat' | 'files'>('chat')
  // 侧栏可收起。讲课时白板铺满整屏更好用，需要看名单或聊天再拉出来。
  // 横屏手机默认收起侧栏：屏幕只有 393px 高，侧栏并排会把白板挤成一条。
  // 竖屏保持展开 —— 侧栏在下面，不抢白板的宽度。
  const [sideOpen, setSideOpen] = useState(() => !isMobileLandscape())
  // 手机上的抽屉：成员 / 聊天（含文件）。桌面不用。
  const [sheet, setSheet] = useState<'none' | 'chat' | 'members'>('none')
  const memberCount = useRoomStore((s) => s.members.size)
  const unread = useChatUnread(mobile ? sheet === 'chat' : sideOpen && sideTab === 'chat')

  // 有人开始共享屏幕时自动切到视频页 —— 老师共享了却没人看见是最常见的抱怨。
  useEffect(() => {
    if (sharerId >= 0) setTab('video')
  }, [sharerId])

  // 被动离开（被请出 / 主持人结束课程 / 顶号）：让提示停留一下，再退回登录页。
  useEffect(() => {
    if (!leftReason) return
    const t = window.setTimeout(() => {
      leave()
      onExit()
    }, 1800)
    return () => window.clearTimeout(t)
  }, [leftReason]) // eslint-disable-line react-hooks/exhaustive-deps

  // 提示条几秒后自动消失。
  useEffect(() => {
    if (!toast) return
    const t = window.setTimeout(() => setToast(null), 4000)
    return () => window.clearTimeout(t)
  }, [toast, setToast])

  if (!client) return null

  const lecture = !!room && (room.flags & RoomFlag.StateLectureMode) !== 0

  return (
    <div className={`classroom${landscape ? ' immersive' : ''}${chromeHidden ? ' chrome-hidden' : ''}`}>
      <header className="cr-head">
        <div className="cr-title">
          <strong>{room?.name ?? '教室'}</strong>
          <span className="cr-roomid">房间号 {session.roomId}</span>
          {lecture && <em className="tag tag-mode">讲课模式</em>}
          {recording.active && <em className="tag tag-rec">● 录制中</em>}
        </div>

        <div className="cr-status">
          <ClassTimer />
          <NetIndicator />
          {/* 连着的时候不说话（腾讯会议也不显示「已连接」）；只有连接中 / 重连中 / 断开才需要人看见 */}
          {conn !== 'connected' && <span className={`conn conn-${conn}`}>{connText(conn)}</span>}
          <span className="cr-user">{session.nick || session.account}</span>
        </div>
        {/* 手机：白板/视频 的切换并进顶栏。单独占一行是 50px，首屏的 6%。 */}
        {mobile && (
          <div className="cr-tabs in-head">
            <button className={tab === 'board' ? 'on' : ''} onClick={() => setTab('board')}>白板</button>
            <button className={tab === 'video' ? 'on' : ''} onClick={() => setTab('video')}>
              视频{sharerId >= 0 ? '·共享' : ''}
            </button>
          </div>
        )}
      </header>

      {room?.notice && (!mobile || !noticeDismissed) && (
        // 手机上公告只在进房时显示一次，点掉就收进「教室信息」。
        // 桌面上一行字无所谓，手机上它是 52px —— 首屏的 6%，
        // 而一节课的公告看一遍就够了。
        <div
          className={`cr-notice${mobile ? ' dismissible' : ''}`}
          onClick={mobile ? dismissNotice : undefined}
          role={mobile ? 'button' : undefined}
        >
          <IcMegaphone size={15} />
          <span>{room.notice}</span>
          {mobile && <em className="cr-notice-x">知道了</em>}
        </div>
      )}

      <PrivateChat />

      <GroupBanner onSwitchRoom={onSwitchRoom} />
      <div className="cr-body">
        <ReactionLayer />
        {/* 投票卡浮在内容区，不挡白板中央 */}
        <PollCard />
        <AttendCard />
        <AttendProgress />
        <CaptionLayer />
        <main className="cr-main">
          {!mobile && <div className="cr-tabs">
            <button
              className="cr-side-toggle"
              onClick={() => setSideOpen((v) => !v)}
              title={sideOpen ? '收起侧栏' : '展开成员与交流'}
            >
              {sideOpen ? '›' : '‹'}
              {!sideOpen && <UnreadBadge unread={unread} />}
            </button>
            <button className={tab === 'board' ? 'on' : ''} onClick={() => setTab('board')}>
              白板
            </button>
            <button className={tab === 'video' ? 'on' : ''} onClick={() => setTab('video')}>
              视频{sharerId >= 0 ? '（有人共享）' : ''}
            </button>
          </div>}

          {tab === 'board' && (
            <WhiteboardTools client={client} onImportDoc={() => setImporting(true)} />
          )}

          <div className="cr-stage">
            {tab === 'board' ? (
              <Whiteboard client={client} />
            ) : (
              <VideoStage localStream={localStream} />
            )}
          </div>

          {/* 视频页里自己已经是宫格的一格，只有看白板时才需要角落小窗。 */}
          {tab === 'board' && <LocalPreview stream={localStream} />}
        </main>

        {!mobile && (
          <aside className={`cr-side${sideOpen ? '' : ' collapsed'}`}>
            {/* 候场名单放最上面：有人在门外等，是当下最该处理的事。 */}
            <WaitingPanel />
            <MemberList />
            <div className="side-tabs">
              <button className={sideTab === 'chat' ? 'on' : ''} onClick={() => setSideTab('chat')}>
                交流
                <UnreadBadge unread={unread} />
              </button>
              <button className={sideTab === 'files' ? 'on' : ''} onClick={() => setSideTab('files')}>
                文件
              </button>
            </div>
            {sideTab === 'chat' ? <Chat /> : <FileTransfer />}
          </aside>
        )}
      </div>

      {/* 手机：侧栏的内容装进两个底部抽屉，内容区拿回整个屏幕。 */}
      {mobile && (
        <>
          <BottomSheet
            open={sheet === 'chat'}
            title="交流"
            onClose={() => setSheet('none')}
            aside={
              <div className="side-tabs">
                <button className={sideTab === 'chat' ? 'on' : ''} onClick={() => setSideTab('chat')}>消息</button>
                <button className={sideTab === 'files' ? 'on' : ''} onClick={() => setSideTab('files')}>文件</button>
              </div>
            }
          >
            {sideTab === 'chat' ? <Chat /> : <FileTransfer />}
          </BottomSheet>
          <BottomSheet open={sheet === 'members'} title={`在场成员 · ${memberCount}`} onClose={() => setSheet('none')}>
            <WaitingPanel />
            <MemberList />
          </BottomSheet>
        </>
      )}

      <Toolbar
        startWith={startWith}
        roomPassword={session.roomPassword}
        onLocalStream={setLocalStream}
        onLeave={() => {
          leave()
          onExit()
        }}
        mobileSheets={
          mobile
            ? {
                openChat: () => setSheet('chat'),
                openMembers: () => setSheet('members'),
                unread,
                memberCount,
              }
            : undefined
        }
        desktopSide={
          mobile
            ? undefined
            : {
                open: sideOpen,
                toggle: () => setSideOpen((v) => !v),
                openChat: () => {
                  setSideOpen(true)
                  setSideTab('chat')
                },
                unread,
                memberCount,
              }
        }
      />


      {/* 音频常驻播放，与当前标签页无关 */}
      <AudioSink />

      {/* 有人申请操作本机桌面时弹出 */}
      <ControlRequestPrompt />

      {importing && <DocImport client={client} onClose={() => setImporting(false)} />}

      <PollResult />
      <AttendResult />
      {toast && (
        <div className="toast" onClick={() => setToast(null)}>
          {toast}
        </div>
      )}

      {conn === 'reconnecting' && (
        <div className="reconnect-mask">
          <div className="reconnect-box">
            <div className="spinner" />
            <p>网络中断，正在重新连接…</p>
            <span>板书与成员状态会在重连后自动恢复</span>
          </div>
        </div>
      )}
    </div>
  )
}

/** 连接状态的中文说明。 */
function connText(s: string): string {
  switch (s) {
    case 'connected':
      return '已连接'
    case 'connecting':
      return '连接中'
    case 'reconnecting':
      return '重连中'
    case 'closed':
      return '已断开'
    default:
      return '未连接'
  }
}

/** 未读角标：有 @我 的显示「@」，否则显示条数；没有未读不渲染。 */
function UnreadBadge({ unread }: { unread: { count: number; mention: boolean } }) {
  if (unread.count === 0) return null
  return (
    <em className={`side-badge${unread.mention ? ' mention' : ''}`} title={`${unread.count} 条未读`}>
      {unread.mention ? '@' : unread.count > 99 ? '99+' : unread.count}
    </em>
  )
}
